import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'
import ResourceTile from './components/resource-tile'
import Paginator from '../utils/paginator'
import FilterButtons from '../elements/filter-buttons'
import ResourceSummary from './components/resource-summary'
import { createSelector } from '@reduxjs/toolkit'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { clearMusicSource } from '../../reducers/sound'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/underline-01.svg'
import { useNavigate } from 'react-router-dom'

const selectPager = state => state.pager.resources
const selectResources = state => state.resources

const makeResources = createSelector(
    [selectPager, selectResources],
    (pager, resources) => {
        if (pager) {
            return resources
                .filter(item => pager.itemIds.indexOf(item.uuid) !== -1)
                .sort((a, b) => {
                    if (pager.itemIds.indexOf(a.id) > pager.itemIds.indexOf(b.id)) {
                        return 1
                    }
                    return -1
                })
        }
        return []
    }
)

const mapStateToProps = (state) => {
    const { keyIssueCategories, nav } = state
    const pager = selectPager(state)
    const resources = makeResources(state)
    const urlCategory = new URLSearchParams(window.location.search).get('category')
    return { pager, resources, keyIssueCategories, navState: nav, urlCategory }
}

const ResourcesIndex = ({ pager, dispatch, resources, keyIssueCategories, navState, urlCategory }) => {
    const componentName = 'Resources'
    const [selectedFilter, setSelectedFilter] = useState(urlCategory || null)
    const navigate = useNavigate()

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState(urlCategory)
            if (navState.theme !== 'grey') {
                dispatch(saveNavTheme('grey'))
            }
        } else if (componentState === 'ANIMATING_IN') {
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (urlCategory !== selectedFilter) {
            setSelectedFilter(urlCategory)
        }
    }, [urlCategory, selectedFilter])

    const handlePreloadingState = async () => {
        let params = {}
        if (urlCategory) {
            params.category = urlCategory
        }
        dispatch(fetchApiData(apiResourceEndpoint('resources', 'LIST'), params))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        dispatch(clearMusicSource())
        dispatch(completeAnimatingIn())
    }

    const handleAnimatingOutState = () => {
        setSelectedFilter(null)
        dispatch(completeAnimatingOut())
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    const handleFilterClick = (filter) => {
        // NOTE: We could have a second click clear the filter but not doing that for now
        if (filter !== selectedFilter) {
            let params = {}
            if (filter) {
                params.category = filter
                navigate(buildRoutePath(routes.resourcesIndex.path, {}, { category: filter }))
            } else {
                navigate(buildRoutePath(routes.resourcesIndex.path, {}, {}))
            }
            setTimeout(() => {
                let app = document.getElementById('app')
                if (app) {
                    app.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }
            }, 100)
            dispatch(fetchApiData(apiResourceEndpoint('resources', 'LIST'), params))
        }
    }

    // clicking the already-selected category button clears the filter
    const handleFilterButtonClick = (filter) => {
        handleFilterClick(filter === selectedFilter ? null : filter)
    }

    const handlePageClick = (page) => {
        let params = { page }
        if (selectedFilter) {
            params.category = selectedFilter
        }
        dispatch(fetchApiData(apiResourceEndpoint('resources', 'LIST'), params))
        setTimeout(() => {
            // make sure the scroll to top on window and containers
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
            let app = document.getElementById('app')
            if (app) {
                app.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }, 100)
    }

    if (componentState === 'PRELOADING') {
        return null
    }

    return (
        <div className="resources-index">
            <div className="container">
                <div className="title-stroke">
                    <h1>Resources</h1>
                    <MarkerStroke />
                </div>
                <p className="page-blurb">
                    <i>Way Outta No Way</i> resources include news stories, reports, videos, academic articles and
                    books by a wide range of authors and organizations. Although most are readily accessible on the
                    internet, some are available only through a paywall, your local library or bookseller.
                </p>

                <FilterButtons onHandleFilterClick={handleFilterButtonClick} selectedFilter={selectedFilter} filterObjects={keyIssueCategories} colorWhenUnfiltered />

                {selectedFilter &&
                    <ResourceSummary selectedFilter={selectedFilter} filterObjects={keyIssueCategories} />
                }

                <div className="resources-list">
                    {resources.map((resource) => {
                        return (<ResourceTile key={resource.uuid} resource={resource} filterObjects={keyIssueCategories} onHandleFilterClick={handleFilterClick} selectedFilter={selectedFilter} />)
                    })}
                </div>
                <Paginator pager={pager} onSetCurrentPage={handlePageClick} />
            </div>
        </div>
    )
}

export default connect(mapStateToProps)(ResourcesIndex)
